import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {QuillModules, CustomOption, QuillToolbarConfig} from 'ngx-quill/config/quill-editor.interfaces';
import {QuillEditorComponent} from 'ngx-quill';
import {cloneDeep} from 'lodash-es';
import {EditorChangeContent, EditorChangeSelection} from "ngx-quill/lib/quill-editor.component";
import {ValidationService} from "../../services/validation/validation.service";
import Quill from 'quill';
const Parchment = Quill.import('parchment');
const Block = Quill.import('blots/block');
const ENTER_KEY = 13;

export interface AttributorOptions {
  scope?: any;
  whitelist?: string[];
}

class IndentAttributor extends Parchment.StyleAttributor {
  constructor(attrName: string, keyName: string, options: AttributorOptions = {}) {
    super(attrName, keyName, options);
  }

  add(node: any, value: any) {
    value = parseInt(value);
    if (value <= 0) {
      this.remove(node)
      return true;
    } else {
      node.style.listStylePosition = 'inside';
      return super.add(node, `${value}em`);
    }
  }

  value(node: HTMLElement) {
    return parseFloat(super.value(node)) || undefined;
  }
}

const IndentStyle = new IndentAttributor('indent', 'text-indent', {
  scope: Parchment.Scope.BLOCK,
  whitelist: ['1em', '2em', '3em', '4em', '5em', '6em', '7em', '8em', '9em']
});
Quill.register(IndentStyle, true);

const insertNewline = (quill: any, range: any, context: any, formattingChanges: any) => {
  if (range.length > 0) {
    quill.scroll.deleteAt(range.index, range.length);  // So we do not trigger text-change
  }
  let lineFormats: any = Object.keys(context.format).reduce((lineFormats: any, format) => {
    if ((Parchment as any).query(format, Parchment.Scope.BLOCK) && !Array.isArray(context.format[format])) {
      lineFormats[format] = context.format[format];
    }
    return lineFormats;
  }, {});
  quill.insertText(range.index, '\n', lineFormats, (Quill as any).sources.USER);
  quill.setSelection(range.index + 1, (Quill as any).sources.SILENT);
  quill.focus();
  Object.keys(context.format).forEach((name) => {
    if (lineFormats[name]) return;
    if (Array.isArray(context.format[name])) return;
    if (name === 'link') return;
    quill.format(name, context.format[name], (Quill as any).sources.USER);
  });
  quill.formatLine(range.index + 1, 1, formattingChanges, (Quill as any).sources.USER);
};

const TopSpacing = new Parchment.StyleAttributor('top-spacing', 'margin-top');
Quill.register(TopSpacing, true);

const BottomSpacing = new Parchment.StyleAttributor('bottom-spacing', 'margin-bottom');
Quill.register(BottomSpacing, true);

// @ts-ignore
class CustomBlock extends Block {
  static create() {
    const node = super.create();
    node.setAttribute('style', 'margin: 0;');
    return node;
  }
}
// @ts-ignore
Quill.register(CustomBlock, true);

@Component({
  selector: 'app-text-editor',
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.scss']
})
export class TextEditorComponent implements OnInit, OnChanges {
  @ViewChild('editor') editor: QuillEditorComponent;

  @Input() public valid: boolean = true;
  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public hint: string;
  @Input() public error: string;
  @Input() public id: string;
  @Input() public required: boolean;
  @Input() public disabled: boolean;
  @Input() public enableHtml: boolean;
  @Input() public saveHtml: boolean;
  @Input() public max: number;

  private _modelHtml: string;
  private _htmlMode: boolean;
  private _model: any;
  @Input() public set model(value: any) {
    this._model = value;
    const editor = this.editor?.quillEditor;
    if (value?.length && editor && !editor.root.textContent.length && editor.root.children.length === 1) {
      value = '';
      this._model = value;
      editor.root.innerHTML = '';
    }
    this.modelChange.emit(value);
  }
  public get model(): any {
    return this._model;
  }
  @Output() public modelChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() public modelChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() public switchToPreview: EventEmitter<any> = new EventEmitter<any>();

  public visited: boolean;
  private _inputDirty: boolean;
  public get inputDirty(): boolean {
    return this._inputDirty;
  }
  @Input() public set inputDirty(value: boolean) {
    this._inputDirty = value;
    this.inputDirtyChange.emit(this._inputDirty);
  }
  @Output() public inputDirtyChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public get modelAsText(): string {
    return ValidationService.getStringModelAsText(this.model);
  }

  public customOptions: CustomOption[] = [
    {
      import: 'attributors/attribute/direction',
      whitelist: null
    },
    {
      import: 'attributors/style/size',
      whitelist: null
    },
    {
      import: 'attributors/class/align',
      whitelist: null
    },
    {
      import: 'attributors/class/background',
      whitelist: null
    },
    {
      import: 'attributors/class/color',
      whitelist: null
    },
    {
      import: 'attributors/class/direction',
      whitelist: null
    },
    {
      import: 'attributors/class/font',
      whitelist: null
    },
    {
      import: 'attributors/class/size',
      whitelist: null
    },
    {
      import: 'attributors/style/align',
      whitelist: null
    },
    {
      import: 'attributors/style/background',
      whitelist: null
    },
    {
      import: 'attributors/style/color',
      whitelist: null
    },
    {
      import: 'attributors/style/direction',
      whitelist: null
    },
    {
      import: 'attributors/style/font',
      whitelist: null
    },
    {
      import: 'attributors/style/size',
      whitelist: null
    }
  ];

  public modules: QuillModules = {
    history: {
      delay: 2000,
      maxStack: 100,
      userOnly: true
    },
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['link'],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'size': [false, '10px', '12px', '14px', '18px', '24px', '32px'] }]
      ],
      handlers: {
        'undo': () => {
          const editor = this.editor.quillEditor as any;
          editor.history.undo();
          this.htmlTextarea.value = this.editor.quillEditor.root.innerHTML;
        },
        'redo': () => {
          const editor = this.editor.quillEditor as any;
          editor.history.redo();
          this.htmlTextarea.value = this.editor.quillEditor.root.innerHTML;
        }
      }
    },
    clipboard: {
      matchVisual: false
    },
    keyboard: {
      bindings: {
        'header shift enter': {
          key: ENTER_KEY,
          shiftKey: true,
          collapsed: true,
          format: ['header'],
          suffix: /^$/,
          handler: (range: any, context: any) => {
            insertNewline(this.editor.quillEditor, range, context, {'header': false, 'top-spacing': '0px'})
            return false;
          }
        },
        'header enter': {
          key: ENTER_KEY,
          shiftKey: false,
          collapsed: true,
          format: ['header'],
          suffix: /^$/,
          handler: (range: any, context: any) => {
            insertNewline(this.editor.quillEditor, range, context, {'header': false, 'top-spacing': '1em'})
            return false;
          }
        },
        'shift enter': {
          key: ENTER_KEY,
          shiftKey: true,
          handler: (range: any, context: any) => {
            insertNewline(this.editor.quillEditor, range, context, {'top-spacing': '0px'});
            return false;
          }
        },
        'enter': {
          key: ENTER_KEY,
          shiftKey: false,
          handler: (range: any, context: any) => {
            insertNewline(this.editor.quillEditor, range, context, {'top-spacing': '1em'});
            return false;
          }
        }
      }
    }
  };
  public editorFocused: boolean;
  public htmlTextarea: any;
  public isCustomHtml: boolean;

  public ngOnInit(): void {
    if (this.enableHtml) {
      const toolbar = this.modules.toolbar as {
        container?: QuillToolbarConfig;
        handlers?: {
          [key: string]: any;
        };
      };
      toolbar.container.push(['edit-html']);
      toolbar.handlers['edit-html'] = () => {
        const editor = this.editor as any;
        const editorNative = editor.elementRef.nativeElement;

        const htmlHidden = this.htmlTextarea.style.display === 'none';

        if (!htmlHidden) {
          this.checkHTML(this.htmlTextarea.value);
          if (!this.isCustomHtml) {
            this.editor.quillEditor.clipboard.dangerouslyPasteHTML(this.htmlTextarea.value, 'silent');
          } else {
            this.switchToPreview.emit();
          }
        } else if(!this.isCustomHtml) {
          const div = document.createElement('div');
          div.innerHTML = this.editor.quillEditor.root.innerHTML.trim();
          this.htmlTextarea.value = this.formatHTML(div, 0).innerHTML;
        }

        if (!this.isCustomHtml) {
          this.toggleButtons(!htmlHidden);
          const editorControl = editorNative.querySelector('.ql-editor');
          editorControl.style.display = !htmlHidden ? '' : 'none';

          this.htmlTextarea.style.display = htmlHidden ? '' : 'none';

          if (htmlHidden) {
            this.htmlTextarea.focus();
            this.htmlTextarea.setSelectionRange(0,0);
            this.htmlTextarea.scrollTop = 0;
            this._htmlMode = true;
          } else {
            editorControl.focus();
            this._htmlMode = false;
          }
        }
      };
      this._modelHtml = cloneDeep(this.model);
      this.checkHTML(this._modelHtml);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model && !changes.model.currentValue && this.editor?.quillEditor) {
      this.editor.quillEditor.clipboard.dangerouslyPasteHTML('', 'silent');
    }
    if (this.enableHtml && this._htmlMode && changes.saveHtml?.currentValue) {
      this.editor.quillEditor.clipboard.dangerouslyPasteHTML(this.htmlTextarea.value, 'silent');
      setTimeout(() => {
        this.model = this.htmlTextarea.value;
      }, 0);
    }
    if (this.enableHtml && changes.model && !changes.model.firstChange) {
      this._modelHtml = cloneDeep(changes.model.currentValue);
      this.checkHTML(this._modelHtml);
      if (this.isCustomHtml) {
        this.switchToHTML();
      } else {
        this.switchToRich();
      }
    }
  }

  private toggleButtons(remove?: boolean): void {
    const editor = this.editor as any;
    const editorNative = editor.elementRef.nativeElement;
    const button = editorNative.querySelector('.ql-edit-html');
    if (remove) {
      button.parentElement.classList.remove('html-active');
    } else {
      button.parentElement.classList.add('html-active');
    }

    const otherButtons = editorNative.querySelectorAll('.ql-formats > *:not(.ql-edit-html)');
    otherButtons.forEach((elem: any) => {
      if (remove) {
        elem.removeAttribute('disabled');
        if (!this.isCustomHtml) {
          elem.parentElement.classList.remove('display-none');
        }
      } else {
        elem.setAttribute('disabled', true);
        if (this.isCustomHtml) {
          elem.parentElement.classList.add('display-none');
        }
      }
    });
  }

  private switchToHTML(): void {
    this.toggleButtons();
    const editor = this.editor as any;
    const editorNative = editor.elementRef.nativeElement;
    const editorControl = editorNative.querySelector('.ql-editor');
    editorControl.style.display = 'none';

    let tag = 'div';
    let doctype: string = '<!doctype html>';
    const findHTML = this._modelHtml?.match(/<(html|HTML)>/);
    if (findHTML) {
      tag = 'html';
      const findDoctype = this._modelHtml?.match(/<[^<>]+>/);
      if (findDoctype?.toString().toLowerCase().match('doctype')) {
        doctype = findDoctype.toString();
      }
    }

    const html = document.createElement(tag);
    html.innerHTML = this._modelHtml;

    this.htmlTextarea.value = tag === 'html' ? doctype + '\n' + this.formatHTML(html, 0, true).outerHTML : this.formatHTML(html, 0).innerHTML;
    this.htmlTextarea.style.display = '';
    this.htmlTextarea.style.height = '40vh';
    this._htmlMode = true;
  }

  private switchToRich(): void {
    this.toggleButtons(true);
    const editor = this.editor as any;
    const editorNative = editor.elementRef.nativeElement;
    const editorControl = editorNative.querySelector('.ql-editor');
    editorControl.style.display = '';
    if (this.htmlTextarea) {
      this.htmlTextarea.style.display = 'none';
    }
    this._htmlMode = false;
  }

  private checkHTML(model: string): void {
    this.isCustomHtml = false;

    const supportedTags = ['a', 'b', 'strong', 'u', 's', 'i', 'em', 'p', 'br', 'ul', 'ol', 'li', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'sub', 'sup'];
    model && (model.match(/<[^<>]+>/gi) || []).forEach((tag: any) => {
      const cleanTag = tag.split(' ')[0].replace('<', '').replace('>', '').replace('/', '');
      if (!supportedTags.includes(cleanTag.toLowerCase())) this.isCustomHtml = true;
    });
  }

  private formatHTML(node: Element, level: number, includeFirst: boolean = false): Element {
    let indentBefore: string = new Array(level++ + 1).join('  '),
      indentAfter: string = new Array(level - 1).join('  '),
      textNode: Text;

    for (let i = 0; i < node.children.length; i++) {
      if (includeFirst || (level > 1 || node.firstElementChild != node.children[i])) {
        textNode = document.createTextNode('\n' + indentBefore);
        node.insertBefore(textNode, node.children[i]);
      }

      this.formatHTML(node.children[i], level);

      if (node.lastElementChild == node.children[i]) {
        textNode = document.createTextNode('\n' + indentAfter);
        node.appendChild(textNode);
      }
    }

    return node;
  }

  public setup(): void {
    this.addTextarea();
    if (this.isCustomHtml) {
      this.switchToHTML();
    }
  }

  private addTextarea(): void {
    const container = this.editor.quillEditor.addContainer('ql-custom');

    this.htmlTextarea = document.createElement('textarea');
    this.htmlTextarea.className = "ql-edit-html-textarea";
    this.htmlTextarea.style.display = "none";

    container.appendChild(this.htmlTextarea);
  }

  public handleContent($event: EditorChangeContent | EditorChangeSelection): void {
    // no-op; comment out this since editor freezes on text cut command,
    /*if ($event.event === 'text-change' && !$event?.text?.replace(/(?:\r\n|\r|\n)/g, '')?.length) {
      this.model = '';
    }*/
  }

  public get requiredButEmpty(): boolean {
    return this.inputDirty && this.required && !this.model;
  }
}
